// src/components/modifiers/ModifierOption.tsx
import React from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { ModifierOption as ModifierOptionType } from '../../types/modifier';

interface ModifierOptionProps {
  option: ModifierOptionType;
  isSelected: boolean;
  quantity: number;
  allowMultiples: boolean;
  onSelect: () => void;
  onQuantityChange: (quantity: number) => void;
  disabled?: boolean;
}

export const ModifierOption: React.FC<ModifierOptionProps> = ({
  option,
  isSelected,
  quantity,
  allowMultiples,
  onSelect,
  onQuantityChange,
  disabled = false,
}) => {
  const price = parseFloat(option.price) || 0;

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuantityChange(quantity + 1);
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity > 1) {
      onQuantityChange(quantity - 1);
    } else {
      onSelect(); // Deselect if quantity becomes 0
    }
  };

  return (
    <div
      className={`flex items-center justify-between py-3 px-0 border-b border-gray-200 transition-all ${
        disabled ? 'opacity-40 cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'
      }`}
      onClick={disabled ? undefined : onSelect}
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Radio/Checkbox - FIXED: allowMultiples=false means RADIO (single), true means CHECKBOX (multiple) */}
        <div className="flex-shrink-0">
          {!allowMultiples ? (
            <input
              type="radio"
              checked={isSelected}
              onChange={onSelect}
              disabled={disabled}
              className="w-5 h-5 border-gray-300 focus:ring-2 cursor-pointer"
              style={{
                accentColor: isSelected ? '#FA2C37' : undefined,
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelect}
              disabled={disabled}
              className="w-5 h-5 border-gray-300 rounded focus:ring-2 cursor-pointer"
              style={{
                accentColor: isSelected ? '#FA2C37' : undefined,
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>

        {/* Option Name */}
        <div className="flex-1">
          <span className="text-gray-900 font-medium">{option.name}</span>
          {!option.isEnabled && (
            <span className="ml-2 text-xs text-red-600">(Unavailable)</span>
          )}
        </div>

        {/* Price - Always show */}
        <span className="text-gray-600 font-medium">
          +${price.toFixed(2)}
        </span>
      </div>

      {/* Quantity Controls (only if allowMultiples and selected) */}
      {allowMultiples && isSelected && (
        <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={handleDecrement}
            disabled={disabled}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <FaMinus className="w-3 h-3 text-gray-600" />
          </button>
          <span className="w-8 text-center font-medium text-gray-900">{quantity}</span>
          <button
            onClick={handleIncrement}
            disabled={disabled}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <FaPlus className="w-3 h-3 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  );
};
